/*
 * 异步状态：loading / empty / 失败。
 *
 * 失败态用**缈缈自己的话**说，不是一张白色的报错卡片。
 *
 * 这符合 CHARACTER.md §8 的 noLie：任何失败都必须如实告知，
 * 「**可以用自己的语气**，但不能伪装成成功，也不能沉默」。
 *
 * 技术原因**不出现在界面上**。
 *
 * 这里曾经有一个「怎么回事」的折叠按钮，展开能看到失败分类、原始信息和
 * 请求编号。它的问题不是信息本身，而是那颗按钮：一行带虚线下划线的灰字，
 * 横在缈缈的台词底下，把一句话重新变回了一张报错卡——正是这个组件要避免的东西。
 *
 * 信息没有丢，改走 console.warn：失败分类、原始信息、请求编号，以及这一块
 * 接上服务端后应该显示什么，全部打在控制台。要排查的人打得开，用户不被打扰。
 * 这和「彻底藏掉」不是一回事——旧项目那句包打天下的「梦境灵力不稳，
 * 请稍后再试试吧~」是连开发者都查不到原因，这里查得到。
 *
 * ⚠ 代价：用户没法把请求编号念给客服。真要做客服工单，编号应该进设置页的
 * 诊断区，而不是回到每条失败提示下面。
 */

import { useEffect, useState } from "react";
import { describeFailure, type ApiError } from "../services/api-client";
import { fallbackLine, type FallbackScene } from "../services/offline-preview";
import { useAppearance } from "../state/session";
import { IconInfo, IconRetry } from "./Icons";
import { Skeleton } from "./ui";

/*
 * 两片翅膀共用同一份形状，左翅靠 CSS 的 scaleX(-1) 翻过去。
 * 同一个 JSX 元素可以被引用两次——它只是不可变的描述，不是实例。
 */
const wing = (
  <svg width="42" height="60" viewBox="0 0 42 60" focusable={false}>
    <path
      className="butterfly__hind"
      d="M1 28 C11 26, 23 30, 27 38 C30 46, 26 55, 18 54.5 C9 54, 2 44, 0.5 35 Z"
    />
    <path
      className="butterfly__fore"
      d="M0.5 25 C3.5 12, 14 3, 28 1 C33 .3, 37.4 1.6, 39 4 C37.6 11, 31 19, 23 24.5 C15 30, 5 33, 1 30.5 Z"
    />
    <circle className="butterfly__spot" cx="26" cy="11" r="2.7" />
    <circle className="butterfly__spot" cx="32.5" cy="8" r="1.5" />
    <circle className="butterfly__spot" cx="17" cy="43" r="2.1" />
  </svg>
);

/**
 * 一只振翅的蝴蝶。
 *
 * 只有翅膀，没有头、触角和身体——这是一枚图腾，不是一只昆虫。
 * 代价是画不出真正的侧飞：侧视时能看到的正是身体，翅膀恰好边缘朝向观察者。
 *
 * 形状交给内联 SVG，所有 transform 都发生在普通 HTML 元素上。不把 transform
 * 加在 SVG 内部元素上是因为 transform-box 在旧版 iOS WebKit 上有坑，
 * 不值得为一个装饰件去赌。
 *
 * ⚠ 与 index.html 首屏那几只是**两份独立实现**。首屏那份不能依赖任何 CSS
 * chunk，颜色只能写死；这里必须走设计 token。改一处记得看另一处。
 */
export function Butterfly({ className = "" }: { className?: string }) {
  return (
    <span className={`butterfly ${className}`.trim()} aria-hidden>
      <span className="butterfly__tilt">
        <span className="butterfly__wing butterfly__wing--left">{wing}</span>
        <span className="butterfly__wing butterfly__wing--right">{wing}</span>
      </span>
    </span>
  );
}

export function LoadingState({ label = "正在读取" }: { label?: string }) {
  return (
    <div className="async-state async-state--loading" role="status">
      <Butterfly className="async-state__butterfly" />
      <p className="async-state__title">{label}</p>
      <Skeleton lines={3} className="async-state__skeleton" />
    </div>
  );
}

export function EmptyState({
  title,
  detail,
  action,
}: {
  title: string;
  detail?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="async-state async-state--empty">
      <span className="async-state__glyph" aria-hidden>
        <IconInfo size={22} />
      </span>
      <p className="async-state__title">{title}</p>
      {detail ? <p className="async-state__detail">{detail}</p> : null}
      {action ? <div className="async-state__actions">{action}</div> : null}
    </div>
  );
}

/**
 * 缈缈口吻的失败提示。
 *
 * 读 useAppearance 是为了拿名字和头像。components/README.md 不许共享组件塞业务
 * Store，那条规则针对的是业务逻辑与请求；这里只读展示用的外观，且如果每个页面
 * 都自己往下传名字和头像，反而更容易出现不一致。
 */
export function MiaoNotice({
  scene,
  error,
  onRetry,
  /** 接上服务端之后这一块会显示什么。只打到 console，给下一个改这段代码的人。 */
  expected,
  /**
   * 只渲染话本身，不套头像与气泡。
   * 视觉小说模式下外面已经有对话框了，再套一层气泡就是气泡里套气泡。
   */
  bare = false,
}: {
  scene: FallbackScene;
  error: ApiError;
  onRetry?: () => void;
  expected?: readonly string[];
  bare?: boolean;
}) {
  const appearance = useAppearance();
  // 台词按场景取一次就固定，重渲染不换句。
  const [line] = useState(() => fallbackLine(scene));
  const info = describeFailure(error);

  // 界面上不再有入口，所以这里必须打出来，否则失败原因就真的没人看得到了。
  useEffect(() => {
    console.warn(
      `[miao] ${scene} 失败：${info.title}\n` +
        `${info.detail}\n` +
        `可重试：${info.retryable ? "是" : "否，重试不会有帮助"}\n` +
        `请求编号：${error.requestId}` +
        (expected && expected.length > 0
          ? `\n接上服务端后这里会显示：\n  - ${expected.join("\n  - ")}`
          : ""),
      error,
    );
  }, [scene, error, info.title, info.detail, info.retryable, expected]);

  const body = (
    <>
      <p className="miao-notice__line">{line}</p>

      {onRetry && info.retryable ? (
        <div className="miao-notice__actions">
          <button type="button" className="miao-notice__retry" onClick={onRetry}>
            <IconRetry size={14} />
            再试一次
          </button>
        </div>
      ) : null}
    </>
  );

  if (bare) return <div className="miao-notice-bare">{body}</div>;

  return (
    <div className="miao-notice" role="status">
      <div className="miao-notice__head">
        {appearance.avatarUrl ? (
          <img
            className="miao-notice__avatar"
            src={appearance.avatarUrl}
            alt=""
            aria-hidden
          />
        ) : (
          <span
            className="miao-notice__avatar miao-notice__avatar--wisp"
            aria-hidden
          />
        )}
        <span className="miao-notice__name">{appearance.name}</span>
      </div>

      <div className="miao-notice__bubble">{body}</div>
    </div>
  );
}

/** 页面级失败。与对话里同一套呈现，只是气泡占满整行。 */
export function PageNotice({
  scene,
  error,
  onRetry,
  expected,
}: {
  scene: FallbackScene;
  error: ApiError;
  onRetry?: () => void;
  expected?: readonly string[];
}) {
  return (
    <div className="page-notice">
      <MiaoNotice
        scene={scene}
        error={error}
        {...(onRetry ? { onRetry } : {})}
        {...(expected ? { expected } : {})}
      />
    </div>
  );
}
